import type { AdmittedGameState, CoreGameState } from './admission';
import type { GameStateConnectionStatus } from './transport';

export type GameStateReadinessReason = 'ready' | 'absent' | 'withdrawn' | 'invalid'
  | 'disconnected' | 'clock-unavailable' | 'receipt-expired' | 'source-stale'
  | 'disputed' | 'unconfirmed-final';
export interface GameStateReadiness {
  /** Preserves evidence for display; never use this field as authority. */
  displayed: CoreGameState | null;
  /** Exact game's selected state, only when every primary-read check passes. */
  canonical: CoreGameState | null;
  reason: GameStateReadinessReason;
}

/** #901 primary-reader boundary. No source election, legacy fallback, score
 * conversion, terminal-market mutation or order authority. The caller must
 * explicitly choose a residence budget; activity for another game and REST
 * provider summaries cannot renew this row. Transport receipt is a local
 * liveness bound, not proof of network transit time or producer observation age.
 */
export function gameStateReadiness(key: string, row: AdmittedGameState | undefined,
  status: GameStateConnectionStatus, nowMonoMs: number, maxResidenceMs: number): GameStateReadiness {
  const displayed = row?.state?.gameKey === key ? row.state : null;
  const unavailable = (reason: GameStateReadinessReason): GameStateReadiness => ({ displayed, canonical: null, reason });
  if (!row) return unavailable('absent');
  if (row.withdrawal === 'invalid-record' || row.state && !displayed) return unavailable('invalid');
  if (row.withdrawal || !displayed) return unavailable('withdrawn');
  if (status !== 'live') return unavailable('disconnected');
  const receipt = row.receivedAtMonoMs;
  if (receipt === undefined || !Number.isFinite(receipt) || receipt < 0
    || !Number.isFinite(nowMonoMs) || nowMonoMs < receipt
    || !Number.isFinite(maxResidenceMs) || maxResidenceMs < 0) return unavailable('clock-unavailable');
  if (nowMonoMs - receipt > maxResidenceMs) return unavailable('receipt-expired');
  if (displayed.stale) return unavailable('source-stale');
  if (displayed.disputed) return unavailable('disputed');
  if (displayed.statusId === 3 && (displayed.finalReason !== 'reported'
    || displayed.awayScore === null || displayed.homeScore === null)) return unavailable('unconfirmed-final');
  return { displayed, canonical: displayed, reason: 'ready' };
}
